/**
 * Per-vehicle factory match perforation tile labels.
 *
 * Temporary override layer — see docs companion OVERRIDES.md
 * (branch docs/pdm-upholstery-v2).
 *
 * PDM does not expose a perforation location attribute today, so we map OEM
 * labels here until PDM or Dato overlay is available.
 *
 * - `value: null` — hide the Perforation tile for that YMM (e.g. Ford Bronco).
 * - `value: "Combo" | "Body" | …` — show the tile with that subtitle.
 * - Vehicles not listed — no Perforation tile (unless PDM sends PF/PL + title).
 */

export type FactoryMatchPerforationLabel =
  | "Insert"
  | "Combo"
  | "Body"
  | "Center"
  | "None"
  | "P7"
  | "P63l"

export type FactoryMatchPerforationEntry = {
  make: string
  model?: string
  /** `null` hides the tile; a string is the UI subtitle. */
  value: FactoryMatchPerforationLabel | null
}

/**
 * Curated YMM → factory OEM perforation location.
 * Add entries as vehicles are verified against legacy Dato / OEM specs.
 */
export const FACTORY_MATCH_PERFORATION_BY_VEHICLE: FactoryMatchPerforationEntry[] =
  [
    // Chevrolet
    { make: "Chevrolet", model: "Colorado", value: "Body" },
    { make: "Chevrolet", model: "Silverado 1500", value: "Body" },
    { make: "Chevrolet", model: "Silverado 2500 HD", value: "Body" },
    { make: "Chevrolet", model: "Silverado 3500 HD", value: "Body" },
    { make: "Chevrolet", model: "Suburban", value: "Body" },
    { make: "Chevrolet", model: "Tahoe", value: "Body" },
    { make: "Chevrolet", model: "Trailblazer", value: null },
    // Ford
    { make: "Ford", model: "Bronco", value: null },
    { make: "Ford", model: "F-150", value: "Combo" },
    { make: "Ford", model: "F-150 Lightning", value: "Combo" },
    // GMC
    { make: "GMC", model: "Acadia", value: "Body" },
    { make: "GMC", model: "Canyon", value: "Body" },
    { make: "GMC", model: "Sierra 1500", value: "Body" },
    { make: "GMC", model: "Sierra 2500 HD", value: "Body" },
    { make: "GMC", model: "Sierra 3500 HD", value: "Body" },
    { make: "GMC", model: "Yukon", value: "Body" },
    { make: "GMC", model: "Yukon XL", value: "Body" },
    // Honda
    { make: "Honda", model: "Accord", value: "Body" },
    { make: "Honda", model: "Civic", value: "Combo" },
    // Hyundai
    { make: "Hyundai", model: "Tucson", value: "P7" },
    // Jeep
    { make: "Jeep", model: "Gladiator", value: null },
    { make: "Jeep", model: "Wrangler", value: null },
    // Toyota
    { make: "Toyota", model: "4Runner", value: "P7" },
    { make: "Toyota", model: "Camry", value: "P63l" },
    { make: "Toyota", model: "Land Cruiser", value: "Body" },
    { make: "Toyota", model: "RAV4 LE/XLE", value: "Body" },
    { make: "Toyota", model: "Tacoma", value: "Body" },
    { make: "Toyota", model: "Tundra", value: "Body" },
  ]

function normalizeVehicleToken(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ")
}

function findPerforationEntry(
  make: string,
  model: string
): FactoryMatchPerforationEntry | undefined {
  const m = normalizeVehicleToken(make)
  const mod = normalizeVehicleToken(model)
  if (!m) {
    return undefined
  }

  return FACTORY_MATCH_PERFORATION_BY_VEHICLE.find((entry) => {
    if (normalizeVehicleToken(entry.make) !== m) {
      return false
    }
    if (!entry.model?.trim()) {
      return true
    }
    return normalizeVehicleToken(entry.model) === mod
  })
}

/**
 * Per-vehicle perforation subtitle for factory match tiles.
 *
 * - `undefined` — not in catalog; omit injected tile (PDM PF/PL + title may still apply).
 * - `null` — explicitly hidden for this YMM.
 * - `string` — show tile with this value.
 */
export function resolveFactoryMatchPerforationForVehicle(
  make: string,
  model: string
): string | null | undefined {
  const entry = findPerforationEntry(make, model)
  if (!entry) {
    return undefined
  }
  return entry.value
}

export function shouldHideFactoryMatchPerforation(
  make: string,
  model: string
): boolean {
  return resolveFactoryMatchPerforationForVehicle(make, model) === null
}
